import { useState, useCallback, useMemo, useEffect } from 'react';
import { Notification } from '../../../components/ui/custom-notification';
import type { TeamMember, TeamMemberDocument, ApiTeamMember, ApiTeamMembersResponse } from '../types/team';
import type { TeamMemberFilters } from '../components/dashboard/team/TeamMemberFilters';
import { getAllTeamMembers, deleteTeamMember } from '../../../services/teamMembersApi';
import { addTeamMembers } from '../../../services/teamMembers';

// Helper function to transform API data to frontend format
const transformApiTeamMemberToFrontend = (apiMember: ApiTeamMember): TeamMember => {
  return {
    id: apiMember.team_member_id.toString(),
    name: `${apiMember.user.first_name} ${apiMember.user.last_name}`,
    role: apiMember.team_member_role,
    email: apiMember.user.email,
    phone: apiMember.user.phone_number || '', // Use phone from API if available
    certifications: apiMember.certificates.map(cert => cert.file_name),
    qualifications: apiMember.certificates.map(cert => cert.file_name),
    documents: apiMember.certificates.map(cert => ({
      id: cert.cert_id.toString(),
      fileName: cert.file_name,
      type: 'certification' as const,
      uploadDate: cert.created_at
    })),
    status: apiMember.status.toLowerCase() as 'active' | 'inactive',
    // API mapping fields
    team_member_id: apiMember.team_member_id,
    user_id: apiMember.user_id,
    project_id: apiMember.project_id,
    project_level_role_id: apiMember.project_level_role_id,
    apiData: apiMember
  };
};

// Helper function to transform frontend data to API format
const transformFrontendTeamMemberToApi = (member: TeamMember): any => {
  return {
    team_member_id: member.team_member_id || member.id || undefined,
    user_id: member.user_id || member.apiData?.user_id || 0,
    project_id: member.project_id || 0,
    project_level_role_id: member.project_level_role_id || 0,
    status: member.status === 'active' ? 'Active' : member.status,
    certificates: member.apiData?.certificates || []
  };
};

// Removed dummy data - now only using API data

export const useTeamMembers = (availableCertifications?: Array<{label: string, value: string}>) => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTeamMember, setSelectedTeamMember] = useState<TeamMember>({
    id: '',
    name: '',
    email: '',
    phone: '',
    role: '',
    certifications: [],
    qualifications: [],
    documents: [],
    status: 'active'
  });
  const [isAddingTeamMember, setIsAddingTeamMember] = useState(false);
  
  // Add filter state
  const [filters, setFilters] = useState<TeamMemberFilters>({
    search: '',
    role: [],
    status: [],
  });

  // Fetch team members from API
  const fetchTeamMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getAllTeamMembers();
      
      if (response && response.success === 1) {
        const transformedMembers = response.data.platformTeamMembers.map(transformApiTeamMemberToFrontend);
        setTeamMembers(transformedMembers);
      } else {
        // No fallback data - show error if API fails
        setTeamMembers([]);
        setError('Failed to fetch team members from API');
      }
    } catch (err) {
      setTeamMembers([]);
      setError('Failed to fetch team members from API');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load team members on component mount
  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  // Filter logic
  const filteredTeamMembers = useMemo(() => {
    return teamMembers.filter(member => {
      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const matchesSearch = 
          member.name.toLowerCase().includes(searchTerm) ||
          member.email.toLowerCase().includes(searchTerm);
        if (!matchesSearch) return false;
      }

      // Role filter - if no roles selected, show all; if roles selected, must match
      if (filters.role && filters.role.length > 0) {
        if (!filters.role.includes(member.role)) {
          return false;
        }
      }

      // Status filter - if no statuses selected, show all; if statuses selected, must match
      if (filters.status && filters.status.length > 0) {
        if (!filters.status.includes(member.status)) {
          return false;
        }
      }

      return true;
    });
  }, [teamMembers, filters]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return filters.search !== '' || filters.role.length > 0 || filters.status.length > 0;
  }, [filters]);

  const handleTeamMemberDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !selectedTeamMember) return;
    
    const newDocuments: TeamMemberDocument[] = Array.from(e.target.files).map(file => ({
      id: crypto.randomUUID(),
      fileName: file.name,
      type: 'other',
      uploadDate: new Date().toISOString()
    }));

    setSelectedTeamMember({
      ...selectedTeamMember,
      documents: [...(selectedTeamMember.documents || []), ...newDocuments]
    });
  };

  const handleSaveTeamMember = async () => {
    // This function is no longer used for saving - the modal handles it directly
    // It's kept for backward compatibility but doesn't make API calls
    return;
  };

  const handleEditTeamMember = (member: TeamMember) => {
    
    // Map API certificates to frontend certifications based on document_tags
    let mappedCertifications: any[] = [];
    
    if (member.apiData?.certificates && member.apiData.certificates.length > 0) {
      // Extract unique document tags from API certificates
      const documentTags = member.apiData.certificates
        .flatMap((cert: any) => cert.document_tags || [])
        .filter((tag: string, index: number, arr: string[]) => arr.indexOf(tag) === index); // Remove duplicates
      
      
      // Map document tags to certification objects by searching through available options
      mappedCertifications = documentTags.map((tag: string) => {
        // Search for matching certification by name (case-insensitive)
        const matchingCert = availableCertifications?.find((cert: {label: string, value: string}) => 
          cert.label.toLowerCase().includes(tag.toLowerCase()) ||
          tag.toLowerCase().includes(cert.label.toLowerCase()) ||
          cert.value.toLowerCase() === tag.toLowerCase()
        );
        
        if (matchingCert) {
          return matchingCert;
        } else {
          // Create a new certification object if no match found
          return {
            label: tag,
            value: tag
          };
        }
      });
      
    } else {
    }
    
    // Create updated member with mapped certifications
    const updatedMember = {
      ...member,
      certifications: mappedCertifications
    };
    
    
    setSelectedTeamMember(updatedMember);
    setIsAddingTeamMember(true);
  };

  const handleViewTeamMemberDocuments = (member: TeamMember) => {
    setSelectedTeamMember(member);
    // Additional logic for viewing documents can be added here
  };

  const handleAddNewTeamMember = () => {
    setSelectedTeamMember({
      id: '',
      name: '',
      role: '',
      email: '',
      phone: '',
      certifications: [],
      qualifications: [],
      documents: [],
      status: 'active',
      apiData: undefined
    });
    setIsAddingTeamMember(true);
  };

  const handleDeleteTeamMember = async (member: TeamMember) => {
    if (!member.team_member_id) {
      Notification.error('Cannot delete team member without API ID');
      return;
    }

    try {
      const success = await deleteTeamMember(member.team_member_id);
      if (success) {
        await fetchTeamMembers();
      }
    } catch (err) {
      Notification.error('Failed to delete team member');
    }
  };

  return {
    teamMembers,
    filteredTeamMembers,
    selectedTeamMember,
    isAddingTeamMember,
    filters,
    hasActiveFilters,
    loading,
    error,
    setSelectedTeamMember,
    setIsAddingTeamMember,
    setFilters,
    handleTeamMemberDocumentUpload,
    handleSaveTeamMember,
    handleEditTeamMember,
    handleViewTeamMemberDocuments,
    handleAddNewTeamMember,
    handleDeleteTeamMember,
    fetchTeamMembers
  };
}; 